import {Button} from '@wandb/components';
import React, {useState} from 'react';

import DangerModal from '../../components/DangerModal';
import {SlurmRunActions} from '../../components/Slurm/SlurmRunActions';
import {useSlurmJobActions} from '../../components/Slurm/useSlurmJobActions';
import {useRunPageHeaderQuery} from '../../generated/graphql';
import {
  STOP_RUN,
  type StopRunMutationData,
  type StopRunMutationVariables,
} from '../../graphql/runs';
import {useMutation} from '../../state/graphql/apolloWithAbort';
import {
  EntityProjectPermissions,
  useViewerPermissionsForEntityProject,
} from '../../util/entityProjectPermissions';
import {useStatsigGateHideStopRunsUI} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {isStopRunAvailable} from '../../util/runs';

type RunActionsDropdownProps = {
  entityName: string;
  projectName: string;
  runName: string;
};

export function RunActionsDropdown({
  entityName,
  projectName,
  runName,
}: RunActionsDropdownProps) {
  const isHideStopRunsEnabled = useStatsigGateHideStopRunsUI();
  const [isStopModalOpen, setStopModalOpen] = useState(false);
  const [stopPressed, setStopPressed] = useState(false);

  const slurm = useSlurmJobActions({entityName, projectName, runName});

  const {data} = useRunPageHeaderQuery({
    variables: {entityName, projectName, runName},
  });
  const [stopRun] = useMutation<StopRunMutationData, StopRunMutationVariables>(
    STOP_RUN
  );

  const run = data?.project?.run;
  const readOnly = data?.project?.readOnly ?? false;
  const {getHasPermission} = useViewerPermissionsForEntityProject(
    projectName,
    entityName
  );
  // Stopping a run is allowed when the project isn't read-only OR the viewer's
  // role explicitly grants run:stop (e.g. a custom role inheriting from Viewer).
  // This unlocks ONLY the Stop-run action — Slurm cancel/requeue has no run:*
  // permission, so it stays gated on read-only (see showSlurmActions below).
  // Mutation auth remains the source of truth server-side.
  const canStopRun =
    !readOnly || getHasPermission(EntityProjectPermissions.StopRun);

  if (!run) {
    return null;
  }

  const stopAvailable =
    !isHideStopRunsEnabled && isStopRunAvailable(run.config ?? '');
  const canBeStopped = !stopPressed && run.state === 'running';
  const showStopRun = canStopRun && stopAvailable && canBeStopped;
  const isRunLive = run.state === 'running';

  // Slurm actions are unrelated to run:stop, so they remain hidden whenever the
  // project is read-only — granting run:stop must not surface Slurm controls.
  const showSlurmActions = slurm.showSlurmActions && !readOnly;

  if (!showSlurmActions && !showStopRun) {
    return null;
  }

  // If Slurm is not active but Stop run is available, show standalone button
  if (!showSlurmActions && showStopRun) {
    return (
      <DangerModal
        open={isStopModalOpen}
        onClose={() => setStopModalOpen(false)}
        trigger={
          <Button
            size="md"
            variant="secondary"
            startIcon="stop"
            onPress={() => setStopModalOpen(true)}>
            Stop run
          </Button>
        }
        message="Are you sure you want to STOP this run? This action cannot be undone!"
        yesText="Stop this run"
        yes={() => {
          stopRun({variables: {id: run.id}});
          setStopPressed(true);
        }}
      />
    );
  }

  // Slurm enabled: show split dropdown button
  return (
    <>
      {showStopRun && (
        <DangerModal
          open={isStopModalOpen}
          onClose={() => setStopModalOpen(false)}
          message="Are you sure you want to STOP this run? This action cannot be undone!"
          yesText="Stop this run"
          yes={() => {
            stopRun({variables: {id: run.id}});
            setStopPressed(true);
          }}
        />
      )}
      <SlurmRunActions
        entityName={entityName}
        projectName={projectName}
        runName={runName}
        jobId={showSlurmActions ? slurm.jobId : ''}
        isRunLive={isRunLive}
        showStopRun={showStopRun}
        onStopRun={() => setStopModalOpen(true)}
      />
    </>
  );
}
